import { useRef, useState, useEffect } from 'react';
import { useThrottleFn } from 'ahooks';

import type { DropdownProps } from 'tea-component';

// 普通模式（2列）的面板宽度，与 index.scss $overlay-width 一致
export const GENRE_SELECT_PANEL_WIDTH = 510;
// App Magic 级联模式面板宽度，与 index.scss .cascade-mode calc(758px + 4px + 4px) 一致
export const GENRE_SELECT_PANEL_WIDTH_CASCADE = 766;
// 组件下拉浮窗距离屏幕右侧的留白
const RIGHT_SPACING = 10;

/**
 * 根据触发器左边缘与视口宽度计算 Dropdown placement。
 * 打开时建议用 CASCADE 宽度预判，避免切到 App Magic 后右侧溢出。
 */
export const calcGenreSelectPlacement = (
  componentLeft: number,
  viewportWidth: number,
  panelWidth: number = GENRE_SELECT_PANEL_WIDTH_CASCADE,
): DropdownProps['placement'] => {
  const safeSpacing = panelWidth + RIGHT_SPACING;
  if (viewportWidth - componentLeft < safeSpacing) {
    return 'bottom-start';
  }
  return 'bottom-start';
};

export default function usePlacement() {
  const componentNode = useRef<HTMLDivElement>(null);

  const [placement, setPlacement] = useState<DropdownProps['placement']>('bottom-start');

  const calcPlacement = () => {
    const componentRect = componentNode.current?.getBoundingClientRect();
    const componentLeft = componentRect?.left || 0;
    setPlacement(calcGenreSelectPlacement(componentLeft, document.body.clientWidth));
  };

  const calcPlacementAtOpen = (): DropdownProps['placement'] => {
    const componentRect = componentNode.current?.getBoundingClientRect();
    const componentLeft = componentRect?.left || 0;
    return calcGenreSelectPlacement(componentLeft, document.body.clientWidth);
  };

  const { run: calcPlacementRun } = useThrottleFn(calcPlacement, {
    leading: true,
    trailing: true,
  });

  useEffect(() => {
    calcPlacementRun();

    window.addEventListener('resize', calcPlacementRun, false);

    return () => {
      window.removeEventListener('resize', calcPlacementRun);
    };
  }, []);

  return {
    componentNode,
    placement,
    calcPlacementAtOpen,
  };
}
